import ProfileOverview from "layouts/pages/profile/profile-overview";
import Settings from "layouts/pages/account/settings";

// Material Dashboard 2 PRO React TS components
import MDAvatar from "components/MDAvatar";
import { Badge } from "@mui/material";

// @mui icons
import Icon from "@mui/material/Icon";

import SyndicatorDirectDashboard from "./pages/dashboard/SyndicatorDirectDashboard";

// Images
import profilePicture from "assets/images/team-3.jpg";
import ApplicationList from "pages/applications/List";
import ApplicationDetail from "pages/applications/Detail";
import {
  BACKGROUND_CHECK_FEATURE,
  BANK_ACCOUNT_FEATURE,
  DASHBOARD_FEATURE,
  MCA_FEATURE,
  READ,
  SETTING_LABELS,
} from "requests/constants";
import Reports from "pages/applications/Reports";
import Syndicators from "./pages/Syndicators/Syndicators";
import {
  loadDynamicDashboardRoutes,
  loadSyndicatorDashboardRoutes,
} from "./utils/loadDynamicDashboards";
import OffersPage from "pages/offers/offers";
import Brokers from "pages/brokers/Brokers";
import { rolePermissionExists } from "utils/CommonUtils";

const routes = [
  {
    type: "single",
    name: localStorage.getItem("fullName"),
    key: "current-tenant",
    icon: <MDAvatar src={profilePicture} alt="Current tenant" size="sm" />,
    component: <ApplicationList />,
  },
  { type: "divider", key: "divider-0" },
  {
    type: "collapse",
    name: "Dashboard Menu",
    key: "dashboard_menu",
    icon: <Icon fontSize="medium">dashboard</Icon>,
    collapse: [
      {
        type: "legacy",
        name: "Dashboard",
        key: "dashboard",
        icon: <Icon fontSize="medium">dashboard</Icon>,
        href: "/#/dashboard",
        permission: { action: READ, resource: "DASHBOARD" },
        feature: DASHBOARD_FEATURE,
      },
    ],
  },
  {
    type: "legacy",
    name: "Deals",
    key: "deals",
    icon: <Icon fontSize="medium">attach_money</Icon>,
    route: "/#/deals",
    permission: { action: READ, resource: "MCA_DEALS.MCA_DEALS" },
    feature: MCA_FEATURE,
    additionalCheck: () => {
      const roleName = localStorage.getItem("roleName");
      return !roleName?.toLowerCase().includes("syndicator");
    },
  },
  {
    type: "single",
    name: "Applications",
    key: "applications",
    icon: <Icon fontSize="medium">receipt_long</Icon>,
    route: "/deal_applications",
    component: <ApplicationList />,
    permission: { action: READ, resource: "MCA_DEALS.MCA_DEAL_APPLICATIONS" },
    additionalCheck: () => {
      const roleName = localStorage.getItem("roleName");
      return !roleName?.toLowerCase().includes("syndicator");
    },
  },
  {
    type: "internal",
    name: "Application Detail",
    key: "application-detail",
    route: "/deal_applications/:id",
    component: <ApplicationDetail />,
    additionalCheck: () => {
      const roleName = localStorage.getItem("roleName");
      return !roleName?.toLowerCase().includes("syndicator");
    },
  },

  {
    type: "legacy",
    name: "Transactions",
    key: "transactions",
    icon: <Icon fontSize="medium">account_balance</Icon>,
    route: "/#/deal_transactions",
    permission: { action: READ, resource: "MCA_DEALS.MCA_MANAGE_TRANSACTIONS" },
    additionalCheck: () => {
      const roleName = localStorage.getItem("roleName");
      return !roleName?.toLowerCase().includes("syndicator");
    },
  },
  {
    type: "single",
    name: "Reports",
    key: "reports",
    icon: (
      <Badge badgeContent="NEW" color="info">
        <Icon fontSize="medium">summarize</Icon>
      </Badge>
    ),
    route: "/reports",
    component: <Reports />,
    permission: { action: READ, resource: "SETTINGS.REPORTS" },
    additionalCheck: () => {
      const roleName = localStorage.getItem("roleName");
      return !roleName?.toLowerCase().includes("syndicator");
    },
  },
  {
    type: "legacy",
    name: "Merchants",
    key: "merchants",
    icon: <Icon fontSize="medium">store</Icon>,
    route: "/#/merchants",
    permission: { action: READ, resource: "SETTINGS.MERCHANTS" },
    additionalCheck: () => {
      const roleName = localStorage.getItem("roleName");
      return !roleName?.toLowerCase().includes("syndicator");
    },
  },
  {
    type: "legacy",
    name: "Meetings",
    key: "meetings",
    icon: <Icon fontSize="medium">groups</Icon>,
    route: "/#/meetings",
    feature: "meeting",
    permission: { action: READ, resource: "SETTINGS.MEETINGS" },
    additionalCheck: () => {
      const roleName = localStorage.getItem("roleName");
      return !roleName?.toLowerCase().includes("syndicator");
    },
  },
  {
    type: "single",
    name: "Offers",
    key: "offers-list",
    icon: <Icon fontSize="medium">payments</Icon>,
    route: "/offers",
    component: <OffersPage />,
    permission: { action: READ, resource: "OFFERS" },
  },
  {
    type: "legacy",
    name: "Syndicators/Brokers",
    key: "syndicators",
    icon: <Icon fontSize="medium">account_circle</Icon>,
    route: "/#/syndicator",
    permission: { action: "ON_MAIN_MENU", resource: "SYNDICATOR_BROKER.SYNDICATOR_BROKER" },
    additionalCheck: () => {
      const hasPermission = (action: any, resource: any) => {
        try {
          const rolePermission = (window as any).rolePermission;
          
          return rolePermissionExists(rolePermission?.permissions || [], action, resource);
        } catch (e) {
          console.error("Permission check failed:", e);
          return false;
        }
      };
  
      const roleName = localStorage.getItem("roleName")?.toLowerCase() || "";
      return (
        !roleName.includes("syndicator") &&
        hasPermission("READ", "SYNDICATOR_BROKER.SYNDICATOR_BROKER")
      );
    },
  },

  {
    type: "single",
    name: "Syndicators",
    key: "syndicators-list",
    icon: <Icon fontSize="medium">account_circle</Icon>,
    route: "/syndicators",
    component: <Syndicators />,
    permission: { action: READ, resource: "NEW_SYNDICATORS" },
    additionalCheck: () => {
      const roleName = localStorage.getItem("roleName");
      return !roleName?.toLowerCase().includes("syndicator");
    },
  },
  {
    type: "single",
    name: "Brokers / ISOs",
    key: "brokers-list",
    icon: <Icon fontSize="medium">diversity_3</Icon>,
    route: "/brokers",
    component: <Brokers />,
    permission: { action: READ, resource: "BROKERS" },
    additionalCheck: () => {
      const roleName = localStorage.getItem("roleName");
      return !roleName?.toLowerCase().includes("syndicator");
    },
  },
  { type: "divider", key: "divider-1" },
  {
    type: "collapse",
    name: "Settings",
    key: "settings",
    icon: <Icon fontSize="medium">settings</Icon>,
    permission: { action: READ, resource: "SETTINGS.SETTINGS_MENU" },
    collapse: [
      {
        type: "legacy",
        name: "Users",
        key: "users",
        href: "/#/settings",
        permission: { action: READ, resource: "SETTINGS.USERS" },
      },
      {
        type: "legacy",
        name: SETTING_LABELS.DATA_MIGRATION,
        key: "idprofile",
        href: "/#/settings",
        permission: { action: READ, resource: "SETTINGS.DATA_MIGRATION" },
        additionalCheck: () => {
          const methodOfMigration = localStorage.getItem("methodOfMigration");
          const isSupportUser = localStorage.getItem("isSupport");

          const isMethod2Migration = methodOfMigration === "method-2";
          const isSupport = isSupportUser === "true";

          return isMethod2Migration && isSupport;
        },
      },

      {
        type: "legacy",
        name: SETTING_LABELS.BANK,
        key: "bank",
        href: "/#/settings",
        permission: { action: READ, resource: "SETTINGS.BANK" },
        feature: BANK_ACCOUNT_FEATURE,
      },
      {
        type: "legacy",
        name: SETTING_LABELS.AUDIT_LOG,
        key: "dealAudit",
        href: "/#/settings",
        permission: { action: READ, resource: "SETTINGS.AUDIT_LOG" },
      },

      {
        type: "legacy",
        name: SETTING_LABELS.BANK_ACC_BALANCE,
        key: "bankDailyAccountBalance",
        href: "/#/settings",
        permission: { action: READ, resource: "SETTINGS.BANK_BALANCE" },
        feature: BANK_ACCOUNT_FEATURE,
      },
      {
        type: "legacy",
        name: "ACH Transaction Files",
        key: "actumTransaction",
        href: "/#/settings",
        permission: { action: READ, resource: "SETTINGS.ACH_TRANSACTION_FILES" },
      },
      {
        type: "legacy",
        name: SETTING_LABELS.DEAL_COLLECTION_LOGS,
        key: "dealCollection",
        href: "/#/settings",
        permission: { action: READ, resource: "SETTINGS.DEAL_COLLECTION_LOGS" },
      },
      {
        type: "legacy",
        name: "ACH Scheduler ID Mapping",
        key: "actumOrderNumberDealMapping",
        href: "/#/settings",
        permission: { action: READ, resource: "SETTINGS.ACH_SCHEDULER_MAPPING" },
      },
      {
        type: "legacy",
        name: SETTING_LABELS.EXPERIAN_REPORT_DATA,
        key: "experianReportData",
        href: "/#/settings",
        permission: { action: READ, resource: "SETTINGS.EXPERIAN_DATA" },
        feature: BACKGROUND_CHECK_FEATURE,
      },
      {
        type: "legacy",
        name: SETTING_LABELS.PARTIES,
        key: "party",
        href: "/#/settings",
        permission: { action: READ, resource: "SETTINGS.PARTIES" },
      },
      {
        type: "legacy",
        name: "Syndicators/Brokers",
        key: "old-syndicators",
        href: "/#/settings",
        permission: { action: READ, resource: "SYNDICATOR_BROKER.SYNDICATOR_BROKER" },
        additionalCheck: () => {
          const roleName = localStorage.getItem("roleName");
          return !roleName?.toLowerCase().includes("syndicator");
        },
      },
      {
        type: "legacy",
        name: SETTING_LABELS.HOLIDAYS_LIST,
        key: "holidaysList",
        href: "/#/settings",
        permission: { action: READ, resource: "SETTINGS.HOLIDAYS_LIST" },
      },
      {
        type: "legacy",
        name: SETTING_LABELS.MCA_AGGREGATED_DATA,
        key: "mcaAggregatedData",
        href: "/#/settings",
        additionalCheck: () => localStorage.getItem("environment")?.toUpperCase() !== "PRODUCTION",
      },
      {
        type: "legacy",
        name: SETTING_LABELS.COLLECTION_SCHEDULE_List,
        key: "collectionScheduleList",
        href: "/#/settings",
        permission: { action: READ, resource: "SETTINGS.COLLECTION_SCHEDULE_LOG" },
        additionalCheck: () => localStorage.getItem("isDemoUser") === "true",
      },
      {
        type: "legacy",
        name: SETTING_LABELS.EMAIL_TEMPLATES,
        key: "emailTemplateList",
        href: "/#/settings",
        additionalCheck: () => localStorage.getItem("isSupportUser") === "true",
      },
      {
        type: "legacy",
        name: SETTING_LABELS.DOCUMENT_TEMPLATE_DATA_FILES,
        key: "documentTemplateDataFields",
        href: "/#/settings",
      },
      {
        type: "legacy",
        name: SETTING_LABELS.DOCUMENT_TEMPLATES,
        key: "documentTemplates",
        href: "/#/settings",
      },
    ],
  },
];

/**
 * This method injects dynamic dashboard routes based on metabase configs
 */
export default async function getDynamicRoutes(): Promise<any[]> {
  const roleName = (localStorage.getItem("roleName") || "").toLowerCase();
  const allowedSyndicatorRoles = ["syndicator", "syndicator(limited)"];
  const isSyndicator = allowedSyndicatorRoles.includes(roleName);

  let dynamicRoutes: any[] = [];

  try {
    if (isSyndicator) {
      const logInUserId = localStorage.getItem("logInUserId");
      if (logInUserId) {
        dynamicRoutes = await loadSyndicatorDashboardRoutes(parseInt(logInUserId, 10));
      }
    } else {
      dynamicRoutes = await loadDynamicDashboardRoutes();
    }
  } catch (error) {
    console.error("Failed to load dynamic dashboards:", error);
  }

  const updatedRoutes: any[] = [...routes];

  if (!dynamicRoutes.length) {
    return updatedRoutes;
  }

  const dashboardMenuIndex = updatedRoutes.findIndex((r) => r.key === "dashboard_menu");

  if (dashboardMenuIndex !== -1 && Array.isArray(updatedRoutes[dashboardMenuIndex].collapse)) {
    const oldMenu = updatedRoutes[dashboardMenuIndex];
    updatedRoutes[dashboardMenuIndex] = {
      ...oldMenu,
      collapse: [
        ...(Array.isArray(oldMenu.collapse) && oldMenu.collapse.length > 0
          ? [oldMenu.collapse[0]]
          : []),
        ...(Array.isArray(dynamicRoutes) ? dynamicRoutes : []),
        ...(Array.isArray(oldMenu.collapse) && oldMenu.collapse.length > 1
          ? oldMenu.collapse.slice(1)
          : []),
      ],
    };
  } else {
    updatedRoutes.push(...dynamicRoutes);
  }

  return updatedRoutes;
}
